import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useEffect, useRef } from 'react';

import {
  getGTMUpsellingEventData,
  isSingleProduct,
  replaceItemData,
  replaceOfferData
} from '~models/upselling';
import { actionCreators as homeActions } from '~screens/Dashboard/screens/Home/redux/actions';
import { PRODUCT_CATEGORIES } from '~screens/Dashboard/screens/Home/components/Menu/constants';
import upsellingActions from '~redux/Upselling/actions';
import { DIMENSIONS, sendGTMEvent } from '~utils/analytics';
import { isCustomPizza } from '~models/product';

import {
  getOfferProduct,
  getTextAndClasses,
  getTransComponentProps,
  getVariantType,
  itemsSelector,
  shoppingCartLoadingSelector
} from './utils';
import { ICON_BY_CATEGORY, ITEM_CATEGORIES, NOUN_NUMBER, VERBS_TENSES } from './constants';

export const useUpsellingOption = ({ index, productId, quantity, upsellingVariant }) => {
  const dispatch = useDispatch();
  const shoppingCartLoading = useSelector(shoppingCartLoadingSelector);
  const byopVariants = useSelector(state => state.customPizza?.helpers?.byop?.variants);
  const sizesAndCrusts = useSelector(state => state.sizesAndCrusts?.data.translations);
  const items = useSelector(itemsSelector);
  const { pathname } = useLocation();
  const {
    menuableId: prevMenuableId,
    variant: { menuableId: variantId, crust, size, deltaPrice },
    isUpgraded,
    isOffer,
    offerGroupId,
    category,
    productName,
    addedFrom
  } = upsellingVariant;
  const textAndClasses = getTextAndClasses(isUpgraded);

  const handleReplaceMenuableId = () => {
    const params = {
      items,
      offerId: productId,
      offerGroupId,
      index,
      data: {
        menuableId: isUpgraded ? prevMenuableId : variantId,
        addedFrom: isUpgraded ? addedFrom : [DIMENSIONS.upselling]
      }
    };
    return isOffer ? replaceOfferData(params) : replaceItemData(params);
  };

  const handleOnClick = () => {
    const itemUUID = items[index]?.uuid;
    dispatch(
      upsellingActions.updateVariants(variants => ({
        ...variants,
        [itemUUID]: {
          ...variants[itemUUID],
          isUpgraded: true
        }
      }))
    );
    dispatch(homeActions.getCartPrices(handleReplaceMenuableId()));
    sendGTMEvent(
      textAndClasses?.gtmEventName,
      getGTMUpsellingEventData({ upsellingVariant, location: pathname, quantity, sizesAndCrusts })
    );
  };

  const productCategory =
    isOffer && isCustomPizza(getOfferProduct(items[index], offerGroupId), byopVariants)
      ? PRODUCT_CATEGORIES.BYOP
      : category;

  const transComponentProps = getTransComponentProps({
    productName: size && !isOffer ? '' : productName,
    variant: size || crust,
    variantType: getVariantType(crust),
    itemCategory: isOffer ? ITEM_CATEGORIES.OFFER : ITEM_CATEGORIES.SINGLE,
    verbTense: isUpgraded ? VERBS_TENSES.PAST : VERBS_TENSES.PRESENT,
    productCategory,
    nounNumber: isSingleProduct(quantity) ? NOUN_NUMBER.SINGULAR : NOUN_NUMBER.PLURAL,
    price: deltaPrice,
    crusts: sizesAndCrusts?.crusts,
    sizes: sizesAndCrusts?.sizes
  });

  return {
    onClickContainer: () => !isUpgraded && handleOnClick(),
    onClickCrossButton: handleOnClick,
    hidden: textAndClasses?.hidden,
    animationHidden: textAndClasses?.animationHidden,
    Icon: ICON_BY_CATEGORY?.[category],
    loading: shoppingCartLoading,
    transComponentProps,
    items
  };
};

export const useFitFontSize = ({ maxHeight }) => {
  const containerRef = useRef(null);

  const onResize = element => {
    const size = window.getComputedStyle(element).getPropertyValue('font-size');
    const textHeight = element?.offsetHeight;
    if (textHeight > maxHeight) {
      element.style.fontSize = `${parseFloat(size) - 1}px`;
    }
  };

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return null;
    }
    const resizeObserver = new ResizeObserver(() => onResize(element));
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, []);

  return containerRef;
};
